import { Box, Text } from '@mantine/core';
import { ActionCard, ButtonRows, type WaRendererProps } from './primitives';

/**
 * Interactive button / list.
 *
 * Renders in BOTH directions — the outbound card is a read-only record of the
 * options that were offered, the inbound one shows the option the customer
 * actually tapped. The inbound case used to have no card at all (the card was
 * `!incoming`-gated), so a list reply arrived as a single line of text.
 */
export function InteractiveMessage({ view, incoming }: WaRendererProps) {
  return (
    <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {incoming && (
        <Text component="span" c={incoming ? 'blue.6' : 'rgba(255,255,255,0.85)'} fw={600}>
          ↩{' '}
        </Text>
      )}
      {view.body ?? view.fallbackLabel}
    </Text>
  );
}

export function InteractiveCard({ view }: WaRendererProps) {
  const payload = view.interactive;
  if (!payload) return null;
  return (
    <ActionCard>
      {payload.interactiveType === 'list' ? (
        <>
          <Box
            style={{
              padding: '10px 14px',
              textAlign: 'center',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--mantine-color-blue-6)',
            }}
          >
            {payload.listButtonText}
          </Box>
          {payload.rows.map((r) => (
            <Box
              key={r.id}
              style={{
                padding: '10px 14px',
                borderTop: '1px solid var(--mantine-color-default-border)',
                // Highlight the row the customer actually picked.
                background:
                  view.interactiveSelectionId === r.id
                    ? 'light-dark(var(--mantine-color-blue-0), var(--mantine-color-dark-5))'
                    : undefined,
              }}
            >
              <Text size="sm" fw={500}>
                {r.title}
              </Text>
              {r.description && (
                <Text size="xs" c="dimmed">
                  {r.description}
                </Text>
              )}
            </Box>
          ))}
        </>
      ) : (
        <ButtonRows buttons={payload.buttons.map((b) => ({ key: b.id, label: b.title }))} />
      )}
    </ActionCard>
  );
}

/** Quick-reply button tap on a template we sent (Meta type `button`). Had no
 *  renderer before — it fell through to a bare `message` string. */
export function ButtonReplyMessage({ view, incoming }: WaRendererProps) {
  return (
    <Text size="sm" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      <Text component="span" c={incoming ? 'blue.6' : 'rgba(255,255,255,0.85)'} fw={600}>
        ↩{' '}
      </Text>
      {view.body ?? view.fallbackLabel}
    </Text>
  );
}
